/**
 * In-flight ship guard for window.smallParcelPackNShipTransactionIds.
 *
 * Entry shape:
 *   { orderId: number, StatusRelativeUri: string|null, pending: boolean }
 *
 * - tryAcquireShipLock in onShipClick atomically claims the slot before Starter HTTP.
 * - checkIfOrderAlreadyInPrinting (pack-and-ship-transaction-model) matches on orderId,
 *   including pending entries.
 * - progress-holder removes committed entries on poll terminal states via
 *   removeEntriesByStatusRelativeUri; pending entries (StatusRelativeUri null) are
 *   cleared via clearPendingOrders on Starter failure.
 */

export const SHIP_IN_FLIGHT_CHANGED_EVENT = "EventShipInFlightChanged";

function ensureArray() {
    if (!window.smallParcelPackNShipTransactionIds) {
        window.smallParcelPackNShipTransactionIds = [];
    }
}

function notifyShipInFlightChanged() {
    document.dispatchEvent(new CustomEvent(SHIP_IN_FLIGHT_CHANGED_EVENT));
}

export function areOrdersInFlight(orderIds) {
    if (!orderIds || !orderIds.length) {
        return false;
    }
    ensureArray();
    return window.smallParcelPackNShipTransactionIds.some((entry) => orderIds.includes(entry.orderId));
}

/**
 * Atomically claim the in-flight slot for these orders.
 * Returns true if this caller acquired the lock (first ship attempt).
 * Returns false if any order is already pending or committed (duplicate attempt).
 */
export function tryAcquireShipLock(orderIds) {
    if (!orderIds || !orderIds.length) {
        return false;
    }
    ensureArray();
    const alreadyTracked = orderIds.some((orderId) =>
        window.smallParcelPackNShipTransactionIds.some(
            (entry) => entry.orderId === orderId && (entry.pending || entry.StatusRelativeUri)
        )
    );
    if (alreadyTracked) {
        return false;
    }
    orderIds.forEach((orderId) => {
        window.smallParcelPackNShipTransactionIds.push({
            orderId: orderId,
            StatusRelativeUri: null,
            pending: true,
        });
    });
    notifyShipInFlightChanged();
    return true;
}

export function commitOrdersWithStatusUri(orderIds, statusRelativeUri) {
    if (!orderIds || !orderIds.length || !statusRelativeUri) {
        return;
    }
    ensureArray();
    orderIds.forEach((orderId) => {
        const pendingEntry = window.smallParcelPackNShipTransactionIds.find(
            (entry) => entry.orderId === orderId && entry.pending
        );
        if (pendingEntry) {
            pendingEntry.StatusRelativeUri = statusRelativeUri;
            pendingEntry.pending = false;
        } else {
            window.smallParcelPackNShipTransactionIds.push({
                orderId: orderId,
                StatusRelativeUri: statusRelativeUri,
                pending: false,
            });
        }
    });
    notifyShipInFlightChanged();
}

export function clearPendingOrders(orderIds) {
    if (!orderIds || !orderIds.length) {
        return;
    }
    ensureArray();
    const before = window.smallParcelPackNShipTransactionIds.length;
    window.smallParcelPackNShipTransactionIds = window.smallParcelPackNShipTransactionIds.filter(
        (entry) => !(orderIds.includes(entry.orderId) && entry.pending)
    );
    if (window.smallParcelPackNShipTransactionIds.length !== before) {
        notifyShipInFlightChanged();
    }
}

export function removeEntriesByStatusRelativeUri(statusRelativeUri) {
    if (!statusRelativeUri) {
        return;
    }
    ensureArray();
    const before = window.smallParcelPackNShipTransactionIds.length;
    window.smallParcelPackNShipTransactionIds = window.smallParcelPackNShipTransactionIds.filter(
        (entry) => entry.StatusRelativeUri != statusRelativeUri
    );
    if (window.smallParcelPackNShipTransactionIds.length !== before) {
        notifyShipInFlightChanged();
    }
}

export function handleStarterFailure(orderIds, resetStateCallback) {
    clearPendingOrders(orderIds);
    if (typeof resetStateCallback === "function") {
        resetStateCallback();
    }
}


